import type { Dictionary } from '@ngrx/entity';
import type {
    DenormalizedBlockEntity,
    DenormalizedColumnEntity,
    DenormalizedGridEntity,
    DenormalizedSectionEntity,
    DenormalizedTabEntity,
    NormalizedBlockEntity,
    NormalizedColumnEntity,
    NormalizedGridEntity,
    NormalizedSectionEntity,
    NormalizedTabEntity,
    WebLegacyWidgetEntity,
} from '@web/types';

/**
 * The six entity dictionaries the page is assembled from, exactly as the store
 * keeps them.
 */
export type StructureDictionaries = {
    blocks: Dictionary<NormalizedBlockEntity>;
    sections: Dictionary<NormalizedSectionEntity>;
    columns: Dictionary<NormalizedColumnEntity>;
    tabs: Dictionary<NormalizedTabEntity>;
    grids: Dictionary<NormalizedGridEntity>;
    widgets: Dictionary<WebLegacyWidgetEntity>;
};

/** A column's children are `{ id, schema }` pairs — the union refs normalizr writes. */
type ColumnChildRef = { id: string; schema: string };

/**
 * One cached node: the store entity it was built from, the assembled children it
 * was built around, and the object handed out. All three are compared by
 * reference and nothing else.
 */
type CacheEntry = {
    src: unknown;
    kids: readonly unknown[];
    out: unknown;
};

/**
 * Denormalizes the page structure the way `normalizr.denormalize` does, but
 * reusing the objects it handed out last time.
 *
 * Normalizr builds its cache *inside* each call and shallow-copies every entity
 * it visits, so a single widget edit hands back a whole new tree — every block,
 * row, column and widget with a fresh identity. `track` keeps the DOM, but every
 * `[entity]` input changes reference, which marks every OnPush view on the page
 * dirty and re-runs its bindings. On a large page that is ~3000 invalidated
 * subtrees for one keystroke.
 *
 * Here a node is rebuilt only when the entity it comes from changed reference,
 * or one of its assembled children did. Everything else is handed back
 * unchanged, so the same edit dirties only the `widget → column → row → block`
 * chain — three nodes instead of three thousand.
 *
 * The reuse is safe to memoize across calls because it is keyed on the whole
 * input to a node: a hit means the entity *and* every child reference are the
 * ones the cached object was built from, so it is structurally identical to what
 * a fresh build would produce. The output is a function of the input alone,
 * never of the sequence of calls that led to it — which is what the property
 * test in `tests/structure-denormalizer.spec.ts` pins down against normalizr.
 *
 * Shapes, which differ per level:
 *
 * ```
 * block.children   : string[]           → sections
 * section.children : string[]           → columns
 * column.children  : { id, schema }[]   → union: widget | grid | tab
 * tab.children     : string[]           → grids
 * grid.children    : string[][]         → widgets, by grid column
 * ```
 */
export const createStructureDenormalizer = () => {
    // The previous call's cache. The current call fills a fresh map, so entities
    // that dropped out of the tree are simply not carried over and are collected
    // — no explicit pruning pass.
    let prev = new Map<string, CacheEntry>();

    return (blockIds: readonly string[], dicts: StructureDictionaries): DenormalizedBlockEntity[] => {
        const { blocks, sections, columns, tabs, grids, widgets } = dicts;
        const next = new Map<string, CacheEntry>();

        const reuse = <T>(id: string, src: unknown, kids: readonly unknown[], build: () => T): T => {
            const hit = prev.get(id);

            if (hit !== undefined && hit.src === src && sameRefs(hit.kids, kids)) {
                next.set(id, hit);

                return hit.out as T;
            }

            const out = build();
            next.set(id, { src, kids, out });

            return out;
        };

        /**
         * Widgets, grids and tabs all normalize into the `widgets` schema, and the
         * selector used to hand normalizr a single `{ ...tabs, ...widgets, ...grids }`
         * bag — an O(n) spread on every recompute. The three dictionaries are read
         * separately here instead, in the precedence that spread produced.
         */
        const lookupWidgetLike = (id: string): unknown => {
            if (grids[id] !== undefined) {
                return grids[id];
            }
            if (widgets[id] !== undefined) {
                return widgets[id];
            }

            return tabs[id];
        };

        const resolveGrid = (id: string, src: NormalizedGridEntity): DenormalizedGridEntity => {
            // string[][] → WebLegacyWidgetEntity[][], one inner array per grid column.
            const gridColumns = src.children.map((column) =>
                column.map((widgetId) => lookupWidgetLike(widgetId) as WebLegacyWidgetEntity)
            );
            // Flattened only to compare child references — the nested arrays
            // themselves are rebuilt each pass and would never compare equal.
            const kids: unknown[] = [];
            for (const column of gridColumns) {
                for (const widget of column) {
                    kids.push(widget);
                }
            }

            return reuse(id, src, kids, () => ({ ...src, children: gridColumns }) as DenormalizedGridEntity);
        };

        const resolveTab = (id: string, src: NormalizedTabEntity): DenormalizedTabEntity => {
            const kids = src.children.map((gridId) => {
                const grid = lookupWidgetLike(gridId);

                return grid === undefined ? undefined : resolveGrid(gridId, grid as NormalizedGridEntity);
            });

            return reuse(id, src, kids, () => ({ ...src, children: kids }) as DenormalizedTabEntity);
        };

        /**
         * Dispatches on the union's `schema` key rather than sniffing the entity,
         * which is what `UnionSchema.denormalizeValue` does.
         */
        const resolveColumnItem = (ref: ColumnChildRef): unknown => {
            const src = lookupWidgetLike(ref.id);

            // A missing entity becomes `undefined` in place, as normalizr's default
            // `fallbackStrategy` leaves it — dropping it instead would shift every
            // index after it.
            if (src === undefined) {
                return undefined;
            }
            if (ref.schema === 'tab') {
                return resolveTab(ref.id, src as NormalizedTabEntity);
            }
            if (ref.schema === 'grid') {
                return resolveGrid(ref.id, src as NormalizedGridEntity);
            }

            // A plain widget is a leaf: its denormalized form is the normalized one,
            // so the store entity is handed back as is. No copy, and its identity is
            // already stable — the entity adapter only replaces the entity it updated.
            return src;
        };

        const resolveColumn = (id: string): DenormalizedColumnEntity | undefined => {
            const src = columns[id];

            if (src === undefined) {
                return undefined;
            }

            const kids = src.children.map(resolveColumnItem);

            return reuse(id, src, kids, () => ({ ...src, children: kids }) as DenormalizedColumnEntity);
        };

        const resolveSection = (id: string): DenormalizedSectionEntity | undefined => {
            const src = sections[id];

            if (src === undefined) {
                return undefined;
            }

            const kids = src.children.map(resolveColumn);

            return reuse(id, src, kids, () => ({ ...src, children: kids }) as DenormalizedSectionEntity);
        };

        const resolveBlock = (id: string): DenormalizedBlockEntity | undefined => {
            const src = blocks[id];

            if (src === undefined) {
                return undefined;
            }

            const kids = src.children.map(resolveSection);

            return reuse(id, src, kids, () => ({ ...src, children: kids }) as DenormalizedBlockEntity);
        };

        const structure = blockIds.map(resolveBlock) as DenormalizedBlockEntity[];

        prev = next;

        return structure;
    };
};

const sameRefs = (a: readonly unknown[], b: readonly unknown[]): boolean => {
    if (a === b) {
        return true;
    }
    if (a.length !== b.length) {
        return false;
    }
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) {
            return false;
        }
    }

    return true;
};
